import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminClient } from "@/api/adminClient";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

export default function AdminPersonas() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const controllerRef = useRef(null);
  const [total, setTotal] = useState(0);
  const { query, update, reset } = useAdminListQuery();

  const load = useCallback(async () => {
    const target = ++generation.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ paged: "true", limit: "25" });
      for (const key of ["q", "status", "source", "offset"]) if (query[key]) params.set(key, query[key]);
      const next = await adminClient(`/admin/personas?${params}`, { signal: controller.signal });
      if (generation.current === target) { setRows(next.items || []); setTotal(Number(next.total) || 0); }
    } catch (e) {
      if (generation.current === target && e.name !== "AbortError")
        setError(e.message || "Could not load personas.");
    } finally {
      if (generation.current === target) setLoading(false);
    }
  }, [query.q, query.status, query.source, query.offset]);

  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
      controllerRef.current?.abort();
    };
  }, [load]);

  return (
    <section
      className="persona-editor mx-auto max-w-6xl"
      aria-label="Persona administration"
    >
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Personas</h1>
          <p className="mt-1 text-muted-foreground">
            Manage consented lived-experience persona drafts and corpus.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Link
            to="/admin/persona-imports"
            className="min-h-11 inline-flex items-center rounded-lg border border-border px-4 py-2 font-semibold text-foreground hover:bg-muted"
          >
            Import from DI Voices
          </Link>
          <Link
            to="/admin/personas/new"
            className="min-h-11 inline-flex items-center rounded-lg bg-teal-700 px-4 py-2 font-semibold text-white"
          >
            New persona
          </Link>
        </div>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search persona name or summary" total={total} loading={loading} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Status<select aria-label="Filter persona status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="retired">Retired</option></select></label>
        <label className="text-sm font-medium">Source<select aria-label="Filter persona source" value={query.source || "all"} onChange={(event) => update({ source: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All sources</option><option value="manual">Manual</option><option value="divoices">DI Voices</option></select></label>
      </AdminListToolbar>
      {loading && <p role="status">Loading personas…</p>}
      {error && (
        <p
          className="mb-4 flex flex-wrap items-center gap-3 text-sm text-destructive"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="min-h-11 rounded-lg border border-current px-4 py-2 font-semibold"
            onClick={() => void load()}
          >
            Retry
          </button>
        </p>
      )}
      {!loading && !error && (
        <div className="overflow-x-auto rounded-2xl border border-border bg-background p-6 shadow-sm">
          <table className="w-full min-w-[42rem] text-sm">
            <caption className="sr-only">Managed personas</caption>
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2">Name</th>
                <th>Slug</th>
                <th>Source</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="py-3 font-medium">{row.name}</td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>{row.source_kind}</td>
                  <td>
                    {row.is_active
                      ? "Active"
                      : row.retired_at
                        ? "Retired"
                        : "Draft"}
                  </td>
                  <td className="text-right">
                    <Link
                      className="min-h-11 inline-flex items-center text-teal-700 font-semibold dark:text-teal-300"
                      to={`/admin/personas/${row.id}`}
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <p className="mt-3 text-sm text-muted-foreground">
              No personas yet.
            </p>
          )}
          <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} disabled={loading} onPage={(offset) => update({ offset }, { resetOffset: false })} />
        </div>
      )}
    </section>
  );
}
